import DialogContent from '@mui/material/DialogContent'
import { DataEditContextTextField } from 'Components/reusable/DataContext/fields/DataEditContextTextField'
import { DataEditContextCountryField } from 'Components/reusable/DataContext/fields/DataEditContextCountryField'
import { DataEditContextPhoneInput } from 'Components/reusable/DataContext/fields/DataEditContextPhoneInput'
import Grid from '@mui/material/Grid2'
import { cvAPI } from 'api/CvAPI'
import { personAPI } from 'api/PersonAPI'
import { CountriesData } from 'api/UtilsAPI'
import ErrorOverlay from 'Components/General/ErrorOverlay'
import newWithEditModal from 'Components/reusable/HOC/newWithEditModal'
import { useNotificationPopup } from 'Components/reusable/Notification'
import { useUser } from 'hooks'
import React, { useEffect, useState } from 'react'
import { IError } from 'types/error'
import { IPerson } from 'types/userInterfaces'
import { About } from 'types/profileInterfaces'
import ProfileImageEditor from './ProfileImageEditor'

/** @notExported */
interface IProfileModalProps {
  /** Country options. */
  countries: CountriesData
  /** Person ID. */
  personId: number
  /** Old profile image. */
  oldImage: string
  /** Function for changing profile image. */
  changeOldImage: (image: string) => void
  /** CV data. */
  data: About
  /** Indicator for submitting updated profile. */
  submitIndicator: boolean
  /** Function for resetting submit indicator. */
  resetSubmit: () => void
  /** Function for closing the modal. */
  onClose: () => void
  /** Is phone required. */
  isPhoneRequired?: boolean
}

/**
 * Profile basic details modal.
 *
 * @returns Component for editing profile basic details.
 * @notExported
 */
const ProfileModal: React.FC<IProfileModalProps> = ({
  countries,
  personId,
  oldImage,
  changeOldImage,
  data,
  submitIndicator,
  resetSubmit,
  onClose,
  isPhoneRequired = false,
}) => {
  const { setRefresh } = useUser()
  const { setSuccessNotificationPopup } = useNotificationPopup()

  const [croppedImage, setCroppedImage] = useState<File>()
  const [backendError, setBackendError] = useState<IError>()

  interface CountryType {
    code: string
    label: string
  }

  useEffect(() => {
    const controller = new AbortController()

    ;(async () => {
      if (submitIndicator === true) {
        try {
          await cvAPI.setCvAbout(data.CV.id, data, controller)
          {
            if (croppedImage) {
              const formData = new FormData()
              formData.append('file', croppedImage)
              formData.append('personId', `${data.id}`)
              await personAPI.setProfileImage(formData, controller)
              // the backend resizes and converts the image, so show the stored one
              changeOldImage(await personAPI.getProfileImage(data.id, controller))
            }
            onClose()
            setSuccessNotificationPopup()
            if (croppedImage) setRefresh(true)
          }
        } catch (error) {
          setBackendError(error as IError)
          resetSubmit()
        }
      }
    })()

    return () => {
      controller.abort()
    }
  }, [
    changeOldImage,
    croppedImage,
    data,
    onClose,
    resetSubmit,
    setRefresh,
    setSuccessNotificationPopup,
    submitIndicator,
  ])

  const countriesList: CountryType[] = []
  for (const property in countries) {
    countriesList.push({ code: property, label: countries[property] })
  }

  if (backendError && backendError.name !== 'CanceledError' && backendError.name !== 'AbortError') {
    return <ErrorOverlay error={backendError} setOpen={setBackendError} />
  }

  return (
    <DialogContent>
      <Grid container spacing={2}>
        <Grid
          size={{
            md: 6,
            sm: 6,
            xs: 12,
          }}
        >
          <ProfileImageEditor
            personId={personId}
            oldImage={oldImage}
            changeOldImage={changeOldImage}
            onCroppedImageChange={setCroppedImage}
            onError={setBackendError}
          />
        </Grid>
        <Grid
          size={{
            md: 6,
            sm: 6,
            xs: 12,
          }}
        >
          <DataEditContextTextField<IPerson> field="firstName" required fullWidth insetLabel />
          <DataEditContextTextField<IPerson> field="lastName" required fullWidth insetLabel />
          <DataEditContextPhoneInput<IPerson>
            field="telephone"
            fullWidth
            insetLabel
            required={isPhoneRequired}
            type="tel"
          />
          <DataEditContextTextField<IPerson> field="streetAddress" fullWidth insetLabel />
          <Grid container spacing={2}>
            <Grid size={6}>
              <DataEditContextTextField<IPerson> field="postalCode" fullWidth insetLabel />
            </Grid>
            <Grid size={6}>
              <DataEditContextTextField<IPerson> field="city" fullWidth insetLabel />
            </Grid>
          </Grid>
          <DataEditContextCountryField<IPerson> field="country" countriesOptions={countriesList} fullWidth insetLabel />
        </Grid>
      </Grid>
    </DialogContent>
  )
}

export default newWithEditModal(ProfileModal)
